export interface FormatOptions {
    time?: boolean;
    asObject?: boolean;
}

export interface ObjectifiedDate {
    Y: number;
    M: number;
    D: number;
    h?: number;
    m?: number;
    s?: number;
}

const toMs = (h = 0, m = 0, s = 0, ms = 0): number =>
    h * 3_600_000 + m * 60_000 + s * 1_000 + ms;

const toSeconds = (h = 0, m = 0, s = 0, ms = 0): number =>
    h * 3600 + m * 60 + s + ms / 1000;

const toMinutes = (h = 0, m = 0, s = 0, ms = 0): number =>
    h * 60 + m + s / 60 + ms / 60_000;

const toHours = (h = 0, m = 0, s = 0, ms = 0): number =>
    h + m / 60 + s / 3600 + ms / 3_600_000;

export const isDateObject = (d: unknown): d is Date =>
    Object.prototype.toString.call(d) === "[object Date]";

export const FromTime = {
    jpnDayMap: {
        0: "日", 1: "月", 2: "火", 3: "水",
        4: "木", 5: "金", 6: "土",
    } as Record<number, string>,

    toMs, toSeconds, toMinutes, toHours,

    /**
     * Format a Date into "YYYY-MM-DD HH:mm:ss" (UTC).
     * @param date Date object
     * @param options time: include time portion; asObject: return ObjectifiedDate
     */
    format(date: Date, options: FormatOptions = {}): string | ObjectifiedDate {
        const opts = { time: true, asObject: false, ...options };
        const iso = date.toISOString();
        if (opts.time) {
            if (opts.asObject)
                return {
                    Y: Number(iso.slice(0, 4)),
                    M: Number(iso.slice(5, 7)),
                    D: Number(iso.slice(8, 10)),
                    h: Number(iso.slice(11, 13)),
                    m: Number(iso.slice(14, 16)),
                    s: Number(iso.slice(17, 19)),
                };
            return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
        }
        if (opts.asObject)
            return {
                Y: Number(iso.slice(0, 4)),
                M: Number(iso.slice(5, 7)),
                D: Number(iso.slice(8, 10)),
            };
        return iso.slice(0, 10);
    },

    /**
     * Convert a date string to the shortest readable locale string.
     * Year > Month > Day > Same day > time only.
     * Currently supports "jpn" only.
     * @param input Date string
     * @param locale Language code (default "jpn")
     */
    toDateTimeShortLocale(input: string, locale: string = "jpn"): string {
        const date = new Date(input);
        const now = new Date();

        if (now.getFullYear() !== date.getFullYear())
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        if (now.getMonth() !== date.getMonth()) {
            if (locale === "jpn") return `${date.getMonth() + 1}月${date.getDate()}日`;
        }

        if (now.getDate() !== date.getDate()) {
            if (locale === "jpn") return `${date.getDate()}日${this.jpnDayMap[date.getDay()]}`;
        }

        if (locale === "jpn")
            return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

        return "";
    },
};

export class cDate {
    private _date: ObjectifiedDate;

    constructor(date: string | Date) {
        const d = isDateObject(date) ? date : new Date(date);
        this._date = FromTime.format(d, { asObject: true }) as ObjectifiedDate;
    }

    /**
     * Add N months to the stored date.
     * @param n Number of months to add
     */
    addMonth(n: number): void {
        // Use Date.UTC so the result stays consistent with toISOString() (UTC-based)
        const d = new Date(Date.UTC(
            this._date.Y,
            (this._date.M - 1) + n,   // month is 0-indexed
            this._date.D,
            this._date.h ?? 0,
            this._date.m ?? 0,
            this._date.s ?? 0,
        ));
        this._date = FromTime.format(d, { asObject: true }) as ObjectifiedDate;
    }

    get dateTime(): string {
        return (
            `${this._date.Y}-` +
            `${String(this._date.M).padStart(2, "0")}-` +
            `${String(this._date.D).padStart(2, "0")} ` +
            `${String(this._date.h ?? 0).padStart(2, "0")}:` +
            `${String(this._date.m ?? 0).padStart(2, "0")}:` +
            `${String(this._date.s ?? 0).padStart(2, "0")}`
        );
    }

    get date(): string {
        return (
            `${this._date.Y}-` +
            `${String(this._date.M).padStart(2, "0")}-` +
            `${String(this._date.D).padStart(2, "0")}`
        );
    }

    get time(): string {
        return (
            `${String(this._date.h ?? 0).padStart(2, "0")}:` +
            `${String(this._date.m ?? 0).padStart(2, "0")}:` +
            `${String(this._date.s ?? 0).padStart(2, "0")}`
        );
    }
}
